// providers/chatDocumentViewerStatus.ts
//
// What the open side viewer (ORA-736) is showing, for the composer's context
// pill: the file's name and the page on its indicator. Kept out of the viewer
// context, which every trigger reads, so a scroll re-renders the pill alone.
// Display only: it is never sent, and it never touches the anchor or thread.

import { useSyncExternalStore } from 'react';

export interface ChatDocumentViewerStatus {
  /** Null until the viewer's metadata names the file. */
  fileName: string | null;
  /** Null until the page count is known. */
  page: number | null;
}

let status: ChatDocumentViewerStatus | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Null when no side viewer is open. */
export const setChatDocumentViewerStatus = (
  next: ChatDocumentViewerStatus | null
): void => {
  if (
    next === status ||
    (next &&
      status &&
      next.fileName === status.fileName &&
      next.page === status.page)
  ) {
    return;
  }

  status = next;
  listeners.forEach((listener) => listener());
};

/** Merge into an open viewer's status; a closed viewer stays closed. */
export const updateChatDocumentViewerStatus = (
  patch: Partial<ChatDocumentViewerStatus>
): void => {
  if (!status) return;
  setChatDocumentViewerStatus({ ...status, ...patch });
};

export const getChatDocumentViewerStatus =
  (): ChatDocumentViewerStatus | null => status;

export const useChatDocumentViewerStatus =
  (): ChatDocumentViewerStatus | null =>
    useSyncExternalStore(subscribe, getChatDocumentViewerStatus, () => null);
